import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../context/I18nContext';
import useDropdown from '../../hooks/useDropdown';
import { getTerminalGroups, hasCustomTerminalGraphic, getTerminalData } from '../../utils/cableLogic';
import { renderTerminalIconSimple } from '../../utils/terminalRenderLogic';
import { getEndpointTerminalCompatibility } from '../../utils/terminalCompatibility';

const TerminalSelector = ({ value, onChange, terminali, className = "", settings = {}, endpointContext = null }) => {
  const { t, language } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [customLength, setCustomLength] = useState('10');
  const [customPeel, setCustomPeel] = useState('3');
  const isCustomStripping = value && value.startsWith('SP') && value !== 'SP';
  const handleDropdownClose = useCallback(() => setSearchTerm(''), []);
  
  // Estrai lunghezza e spelatura dal valore corrente
  const parseCurrentValues = () => {
    if (isCustomStripping) {
      const match = value.match(/^SP(\d+)\/(\d+)$/);
      if (match) {
        return { length: match[1], peel: match[2] };
      }
      // Fallback per formato vecchio SPx
      const length = value.substring(2);
      return { length: length, peel: '3' };
    }
    return { length: customLength, peel: customPeel };
  };
  
  const { length: currentCustomLength, peel: currentCustomPeel } = parseCurrentValues();

  const { isOpen, toggleDropdown, closeDropdown, dropdownRef } = useDropdown(false, {
    autoFocus: null,
    onClose: handleDropdownClose,
    closeOnClickOutside: false
  });

  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0 });
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [hoveredTerminal, setHoveredTerminal] = useState(null);
  const menuRef = useRef(null);

  const recalcMenuPosition = useCallback(() => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    // For position: fixed use viewport coords (no scroll offset)
    setMenuStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [dropdownRef]);

  useEffect(() => {
    if (!isOpen) return;
    recalcMenuPosition();
    const onScroll = () => recalcMenuPosition();
    const onResize = () => recalcMenuPosition();
    const onDocMouseDown = (e) => {
      if (!dropdownRef.current || !menuRef.current) return;
      const inTrigger = dropdownRef.current.contains(e.target);
      const inMenu = menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) {
        closeDropdown();
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [isOpen, closeDropdown, dropdownRef, recalcMenuPosition]);

  // Nascondi il tooltip quando il dropdown si chiude
  useEffect(() => {
    if (!isOpen) {
      setHoveredTerminal(null);
    }
  }, [isOpen]);

  const terminalGroups = useMemo(() => getTerminalGroups(terminali, language), [terminali, language]);

  const compatibleGroups = useMemo(() => {
    const compatible = {};
    Object.entries(terminalGroups).forEach(([groupName, terminals]) => {
      const options = terminals.filter(([cod]) => getEndpointTerminalCompatibility({
        terminalCode: cod,
        terminali,
        endpointContext,
        settings,
        allowBelowMin: true
      }).isCompatible);

      if (options.length > 0) compatible[groupName] = options;
    });
    return compatible;
  }, [terminalGroups, terminali, endpointContext, settings]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return compatibleGroups;

    const filtered = {};
    Object.entries(compatibleGroups).forEach(([groupName, terminals]) => {
      const filteredTerminals = terminals.filter(([cod, term]) =>
        cod.toLowerCase().includes(searchTerm.toLowerCase()) ||
        term.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (filteredTerminals.length > 0) {
        filtered[groupName] = filteredTerminals;
      }
    });

    return filtered;
  }, [compatibleGroups, searchTerm]);

  const handleTerminalSelect = (terminalCode) => {
    if (terminalCode === 'SP') {
      onChange(`SP${customLength}/${customPeel}`);
    } else {
      onChange(terminalCode);
    }
    closeDropdown();
  };

  const handleCustomLengthChange = (newLength) => {
    setCustomLength(newLength);
    if (value === 'SP' || isCustomStripping) {
      onChange(`SP${newLength}/${currentCustomPeel}`);
    }
  };

  const handleCustomPeelChange = (newPeel) => {
    setCustomPeel(newPeel);
    if (value === 'SP' || isCustomStripping) {
      onChange(`SP${currentCustomLength}/${newPeel}`);
    }
  };

  const displayValue = isCustomStripping ? 'SP' : value;
  const safeTerminali = useMemo(() => terminali || {}, [terminali]);
  
  // Usa getTerminalData per ottenere il nome tradotto
  const currentTerminalData = useMemo(() => {
    if (!value || value === 'SP' || isCustomStripping) {
      return null;
    }
    return getTerminalData(value, { ...settings, lingua: language }, safeTerminali);
  }, [value, settings, safeTerminali, isCustomStripping, language]);

  const currentCompatibility = useMemo(() => {
    if (!value) return { isCompatible: true };
    const code = isCustomStripping ? 'SP' : value;
    return getEndpointTerminalCompatibility({
      terminalCode: code,
      terminali: safeTerminali,
      endpointContext,
      settings,
      // Il valore già assegnato va verificato sulla composizione reale, come
      // negli warning delle tessere. La possibilità teorica di aggiungere un
      // secondo cavo vale solo quando proponiamo le alternative nel menu.
      allowMultiplePotential: false
    });
  }, [value, isCustomStripping, safeTerminali, endpointContext, settings]);

  const renderTerminalOption = (cod, term, isSelected = false) => {
    const hasCustom = hasCustomTerminalGraphic(cod);

    return (
      <div 
        className="flex items-start gap-3 py-1"
        onMouseEnter={(e) => handleTerminalHover({ code: cod, ...term }, e)}
        onMouseLeave={handleTerminalLeave}
      >
        <div className="flex items-center justify-center flex-shrink-0 self-center">
          {hasCustom ? (
            <div className="w-14 h-7 flex items-center justify-center">
              {renderTerminalIconSimple(cod, 56, 28, safeTerminali)}
            </div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-blue-600"></div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className={`font-medium text-sm ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
            {cod}
          </div>
          <div className="text-xs text-gray-500 break-words leading-relaxed">
            {term.nome}
          </div>
        </div>
      </div>
    );
  };

  const handleTerminalHover = (terminal, event) => {
    setHoveredTerminal(terminal);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipStyle({
      top: rect.top - 10,
      left: rect.right + 40,
      zIndex: 10000
    });
  };

  const handleTerminalLeave = () => {
    setHoveredTerminal(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>

      {/* Trigger button - MIGLIORATO per SP */}
      <button
        type="button"
        onClick={toggleDropdown}
        className={`w-full px-3 py-2 border rounded-md focus:ring-2 bg-white flex items-center justify-between hover:bg-gray-50 transition-colors text-left min-h-[44px] ${currentCompatibility.isCompatible ? 'focus:ring-blue-500' : 'border-red-500 focus:ring-red-400'}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center flex-shrink-0 self-center">
            {hasCustomTerminalGraphic(isCustomStripping ? 'SP' : value) ? (
              <div className="w-14 h-7 flex items-center justify-center">
                {renderTerminalIconSimple(isCustomStripping ? 'SP' : value, 56, 28, safeTerminali)}
              </div>
            ) : (
              <div className="w-4 h-4 rounded-full bg-blue-500 border border-blue-600"></div>
            )}
          </div>

          <div className="flex-1 min-w-0 flex items-center justify-between">
            <div className="min-w-0 flex-1">
              {(value === 'SP' || isCustomStripping) ? (
                <div>
                  <div className="font-medium text-sm">SP</div>
                  <div className="text-xs text-gray-500">{t('selectors.stripping')}</div>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-sm">{displayValue}</div>
                  <div className="text-xs text-gray-500 break-words leading-relaxed">
                    {currentTerminalData?.nome || t('selectors.terminalNotFound')}
                  </div>
                </div>
              )}
            </div>

            {(value === 'SP' || isCustomStripping) && (
              <div className="flex flex-col gap-1 ml-3 flex-shrink-0">
                <div className="flex items-center gap-1 justify-end">
                  <span className="text-xs text-gray-600 font-medium">{t('selectors.lengthLabel')}</span>
                  <input
                    type="number"
                    value={currentCustomLength}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCustomLengthChange(e.target.value || '0');
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500 [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-inner-spin-button]:hover:opacity-100 [&::-webkit-inner-spin-button]:focus:opacity-100"
                    min="0"
                    max="100"
                    placeholder={t('selectors.length')}
                  />
                  <span className="text-xs text-gray-500 font-medium">mm</span>
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <span className="text-xs text-gray-600 font-medium">{t('selectors.peelLabel')}</span>
                  <input
                    type="number"
                    value={currentCustomPeel}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCustomPeelChange(e.target.value || '0');
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500 [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-inner-spin-button]:hover:opacity-100 [&::-webkit-inner-spin-button]:focus:opacity-100"
                    min="0"
                    max="50"
                    placeholder={t('selectors.peel')}
                  />
                  <span className="text-xs text-gray-500 font-medium">mm</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center flex-shrink-0 ml-2 self-center">
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {!currentCompatibility.isCompatible && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {t('selectors.incompatibleTerminalSection')}
        </p>
      )}

      {/* Dropdown panel via portal to avoid clipping */}
      {isOpen && ReactDOM.createPortal(
        <div ref={menuRef} className="fixed bg-white border border-gray-200 rounded-md shadow-lg max-h-96 ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden flex flex-col" style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width, zIndex: 99999 }}>
          {/* Campo di ricerca */}
          <div className="p-2 border-b border-gray-200 flex-shrink-0">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={t('selectors.searchTerminal')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Lista terminali - SCORREVOLE */}
          <div className="flex-1 overflow-auto p-2 space-y-1">
            {Object.keys(filteredGroups).length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-4">
                {Object.keys(compatibleGroups).length === 0
                  ? t('selectors.noCompatibleTerminal')
                  : t('selectors.noTerminalFound')}
              </div>
            ) : (
              Object.entries(filteredGroups).map(([groupName, terminals], groupIndex) => (
                <div key={groupName} className={`mb-2 last:mb-0 ${groupIndex > 0 ? 'border-t-2 border-gray-300 pt-2' : ''}`}>
                  <h4 className="text-xs font-semibold text-gray-600 px-2 py-1 bg-gray-100 rounded-sm sticky top-0">{groupName}</h4>
                  <ul className="space-y-0 mt-1">
                    {terminals.map(([cod, term]) => (
                      <li
                        key={cod}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm border-b-2 border-gray-200 last:border-b-0
                          ${value === cod ? 'bg-blue-100 text-blue-800 font-semibold' : ''}
                        `}
                        onClick={() => handleTerminalSelect(cod)}
                      >
                        {renderTerminalOption(cod, term)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>, document.body
      )}

      {/* Tooltip per informazioni terminale */}
      {hoveredTerminal && ReactDOM.createPortal(
        <div 
          className="fixed bg-blue-50 border border-gray-200 rounded-md shadow-lg p-3 z-50"
          style={tooltipStyle}
        >
          <div>
            <div className="text-sm font-medium text-slate-800 mb-2">
              🔧 {t('selectors.customStripping')}
            </div>
            <div className="text-xs text-slate-700">
              <ul className="space-y-1">
                <li className="flex items-center">
                  <span className="w-1 h-1 bg-slate-500 rounded-full mr-2 flex-shrink-0"></span>
                  <span>{t('selectors.lengthWithUnit')} <span className="font-semibold">{hoveredTerminal.len || '0'} mm</span></span>
                </li>
                <li className="flex items-center">
                  <span className="w-1 h-1 bg-slate-500 rounded-full mr-2 flex-shrink-0"></span>
                  <span>{t('selectors.peelWithUnit')} <span className="font-semibold">{hoveredTerminal.peel || '0'} mm</span></span>
                </li>
              </ul>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
};

export default TerminalSelector;
